import type { Project } from "../lib/project";
import { tx } from "../i18n";
import "./ProjectScope.css";

/* ══ 접근 범위 — MCP 토큰(설정 › MCP 토큰)과 계정(설정 › 사용자)이 같이 쓴다 ══
   값은 셋이다: `null` 전체 · 배열 그 프로젝트들 · 빈 배열 아무것도. 셋째는 글자로 경고한다 — 둘을 접지 않는다
   (docs/decisions/agent-token.md · user-scope.md). */

export type ProjectName = Pick<Project, "id" | "name">;

/** 범위 칸 — 전체 · 프로젝트 이름들 · 아무것도 못 봄. 모르는 id 는 id 그대로. */
export function ScopeText({ projects, names }: { projects: string[] | null; names: ProjectName[] }) {
  if (projects === null) return <span>{tx("projectScope.all")}</span>;
  // 표 칸에는 짧게 — 모양(⚠)과 글자가 함께 간다. 긴 경고는 고르기 안에서 한다.
  if (projects.length === 0)
    return (
      <span className="b-badge b-badge-warning" title={tx("projectScope.none")}>
        <span aria-hidden="true">⚠</span> {tx("projectScope.noneShort")}
      </span>
    );
  return <span>{projects.map((id) => names.find((p) => p.id === id)?.name ?? id).join(", ")}</span>;
}

/** 범위 고르기 — 전체(라디오) 또는 프로젝트 체크. 「고르기」인데 하나도 안 골랐으면 그렇다고 **글자로** 말한다. */
export function ScopePicker({
  value,
  projects,
  onChange,
  name,
  disabled,
}: {
  value: string[] | null;
  projects: ProjectName[];
  onChange: (v: string[] | null) => void;
  /** 라디오 묶음 이름 — 한 화면에 여럿이 서도 안 섞이게. */
  name: string;
  disabled?: boolean;
}) {
  return (
    <fieldset className="pscope" disabled={disabled}>
      <legend className="b-field-label">{tx("projectScope.title")}</legend>
      <label className="b-check">
        <input type="radio" name={name} checked={value === null} onChange={() => onChange(null)} />
        {tx("projectScope.all")}
      </label>
      <label className="b-check">
        <input type="radio" name={name} checked={value !== null} onChange={() => onChange(value ?? [])} />
        {tx("projectScope.pick")}
      </label>
      {value !== null ? (
        <div className="pscope__list">
          {projects.length === 0 ? <p className="caption">{tx("projectScope.noProjects")}</p> : null}
          {projects.map((p) => (
            <label key={p.id} className="b-check">
              <input
                type="checkbox"
                checked={value.includes(p.id)}
                onChange={(e) => onChange(e.target.checked ? [...value, p.id] : value.filter((x) => x !== p.id))}
              />
              {p.name} <span className="caption">{p.id}</span>
            </label>
          ))}
          {value.length === 0 ? <p className="caption">{tx("projectScope.none")}</p> : null}
        </div>
      ) : null}
    </fieldset>
  );
}
